import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { getBorderRadius, HorizontalCard } from 'components/Card';
import { LightGrayLabel } from 'components/labels';
import { Text } from 'components/Text';
import { TrackingEvents, useTrackingDispatch } from 'modules/analytics';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { GENERAL_STATUS_MAP, StatusBadge } from 'modules/status-badge';
import { DEFAULT_TIMEZONE } from 'utils/date';
import { useWorkPeriods } from '../hooks';
import { Month, Week } from '../types';
import {
  getMonthLabelWithTimezone,
  getWeekLabelWithTimezone,
  getWorkedTimeLabel
} from '../utils';
import { WorkWeeksSkeleton } from './WorkWeeksSkeleton';

const WeekView = ({
  week,
  index,
  totalLength
}: {
  week: Week & { weekId: string };
  index: number;
  totalLength: number;
}) => {
  const navigate = useNavigate();
  const dispatch = useTrackingDispatch();

  if (!week.calendarWeekStart || !week.calendarWeekEnd) return null;

  const dateLabel = getWeekLabelWithTimezone(
    week.calendarWeekStart,
    week.calendarWeekEnd,
    DEFAULT_TIMEZONE
  );

  const handleClick = () => {
    dispatch({
      type: TrackingEvents.WEEK_BUTTON_CLICKED,
      payload: { weekId: week.weekId }
    });

    navigate(`${week.weekId}`);
  };

  const borderRadius = getBorderRadius(index, totalLength);

  return (
    <Grid xs={12} item={true}>
      <HorizontalCard
        title={getTransWithParams('abbreviations.calendarWeekWithNumber', {
          weekNumber: week.calendarWeek
        })}
        subtitle={dateLabel}
        onClick={handleClick}
        dataTestId={`week-card-${week.weekId}`}
        borderRadius={borderRadius}
        rightContent={
          <>
            <Text variant="subtitle1" align="right" sx={{ mb: 1 }}>
              {getWorkedTimeLabel(week.expectedMinutes, week.reportedMinutes)}
            </Text>

            <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
              <StatusBadge
                status={week.weekStatus}
                statusMap={GENERAL_STATUS_MAP}
              />
            </Box>
          </>
        }
      />
    </Grid>
  );
};

export const MonthView = ({ month }: { month: Month }) => {
  const { workPeriods } = useWorkPeriods();

  if (!workPeriods) return null;

  const { weeks } = workPeriods;
  const label = getMonthLabelWithTimezone(
    month.month,
    month.year,
    DEFAULT_TIMEZONE
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Text variant="h5" align="center" sx={{ mb: 2 }}>
        {label}
      </Text>
      <Grid container spacing={0}>
        {month?.weeks?.map((weekId: string, index) => (
          <WeekView
            key={'week-' + weekId}
            week={{ ...weeks[weekId], weekId }}
            index={index}
            totalLength={month.weeks.length}
          />
        ))}
      </Grid>
    </Box>
  );
};

export const WorkWeeks = () => {
  const { workPeriods, isLoading } = useWorkPeriods();

  const months = workPeriods?.months || [];

  if (isLoading) {
    return <WorkWeeksSkeleton />;
  }

  if (workPeriods && !months.length)
    return (
      <Box sx={{ textAlign: 'center', p: '0 30px', mt: 10 }}>
        <LightGrayLabel>{getTrans('noWeeksFound.label.body1')}</LightGrayLabel>
        <br />
        <LightGrayLabel>{getTrans('noWeeksFound.label.body2')}</LightGrayLabel>
      </Box>
    );

  return (
    <Box sx={{ mt: 10 }}>
      {months.map((month: Month, index: number) => (
        <MonthView key={'month-' + month.month + '-' + index} month={month} />
      ))}
    </Box>
  );
};
